import classNames from 'classnames';
import React from 'react';

import { mergeProps } from '@react-aria/utils';

import type { LabelPosition } from '@react-types/shared';

import type { FieldProps } from '../../interface-and-type';
import { DatePickerLabel } from '../date-picker-label';
import { Flex } from '../date-picker-layout/flex';

interface IProps extends FieldProps {
  labelPosition?: LabelPosition;
  required?: boolean;
  wrapperClassName?: string;
}

export const LocalDatePickerLabel = React.forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const {
    label,
    labelPosition = 'top',
    labelAlign,
    isRequired,
    necessityIndicator,
    includeNecessityIndicatorInAccessibilityName,
    children,
    labelProps,
    elementType,
    required,
    wrapperClassName
  } = props;

  const labelWrapperClass = React.useMemo(() => {
    return classNames('label-style', wrapperClassName, {
      'positionTop-class': labelPosition === 'top',
      'positionSide-class': labelPosition === 'side'
    });
  }, [labelPosition, wrapperClassName]);

  return (
    <div ref={ref} className={labelWrapperClass}>
      <DatePickerLabel
        {...labelProps}
        elementType={elementType}
        includeNecessityIndicatorInAccessibilityName={includeNecessityIndicatorInAccessibilityName}
        isRequired={isRequired}
        labelAlign={labelAlign}
        labelPosition={labelPosition}
        necessityIndicator={necessityIndicator}
      >
        {label}
        {required && <span className="text-normal pl-px text-rose-600">*</span>}
      </DatePickerLabel>
      <Flex direction="column" UNSAFE_className={'help-text-class'}>
        {children}
      </Flex>
    </div>
  );
});

LocalDatePickerLabel.displayName = 'LocalDatePickerLabel';

export const DatePickerContainer = React.forwardRef<HTMLDivElement, IProps>(
  ({ children, ...wrapperProps }, ref) => {
    if (wrapperProps.label) {
      return (
        <LocalDatePickerLabel ref={ref} {...wrapperProps}>
          {React.cloneElement(
            children,
            mergeProps(children.props, {
              className: 'labelStyles'
            })
          )}
        </LocalDatePickerLabel>
      );
    }

    return <>{React.cloneElement(children, mergeProps(children.props, { ref }))}</>;
  }
);

DatePickerContainer.displayName = 'DatePickerContainer';
